import React, { useState, useEffect } from 'react';
import { localToday, dateStr , classStartsAt} from '../utils/dates';
import { isComped, balanceAfterCheckIn , sessionCost} from '../utils/comp';
import { checkWeeklyCap, nextMakeupCredit } from '../utils/practiceWeek';
import { db, auth } from '../firebase';
import {
  collection, getDocs, getDoc, addDoc, updateDoc, doc, deleteDoc, query, where, serverTimestamp
, increment} from 'firebase/firestore';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { getAllParentUids } from '../utils/athleteLinks';
import {
  Box, Typography, Paper, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, CircularProgress, Avatar, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { logEvent } from '../utils/logEvent';

// "Most active" means recent attendance, not lifetime totals — a player who
// came every week two years ago is not who you want at the top of a check-in
// list today. 30 days: a month is what "who is coming at the moment" means to
// anyone running a session, and at 90 an athlete who stopped in July still
// outranked one who started in September.
const ACTIVITY_WINDOW_DAYS = 30;

// Collapsed labels for the closed dropdown, which is narrower than the menu.
const SORT_LABELS = {
  name: 'A–Z',
  active: 'Most active',
  checkedin: 'Checked in',
  boys: 'Boys',
  girls: 'Girls',
};
// The same two filters on an adult session, where "boys" would be odd.
const ADULT_SORT_LABELS = { ...SORT_LABELS, boys: 'Men', girls: 'Women' };

function CheckIn() {
  const [classes, setClasses] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [athleteActivity, setAthleteActivity] = useState({});
  const [athleteSort, setAthleteSort] = useState('name');   // 'name' | 'active' | 'checkedin' | 'boys' | 'girls'
  const [attendance, setAttendance] = useState([]);
  // The full ledger, plans and credit list — everything checkWeeklyCap needs.
  // attendance above holds only the selected class; the weekly cap has to see
  // the whole week, and the ledger is already being read below anyway.
  const [allAttendance, setAllAttendance] = useState([]);
  const [plansById, setPlansById] = useState({});
  const [makeupCredits, setMakeupCredits] = useState([]);
  const [capWarning, setCapWarning] = useState(null);
  const [shirtAthleteIds, setShirtAthleteIds] = useState(new Set());
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(null);
  const [search, setSearch] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [isParentOrAthlete, setIsParentOrAthlete] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [coachData, setCoachData] = useState(null);
  const [subReason, setSubReason] = useState('');
  const [subClass, setSubClass] = useState(null);
  const [subSending, setSubSending] = useState(false);
  const [subSent, setSubSent] = useState(false);
  const [coachMode, setCoachMode] = useState('checkin'); // 'checkin' | 'sub'
  const [allCoaches, setAllCoaches] = useState([]);
  const [subDialog, setSubDialog] = useState(false);
  const [msgDialog, setMsgDialog] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [classCounts, setClassCounts] = useState({});
  const [myClassStatus, setMyClassStatus] = useState({}); // classId -> reserved | waitlisted | checked_in
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [classFilters, setClassFilters] = useState(['today']);

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date(); 
      const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      const user = auth.currentUser;

      try {
        const userDoc = user ? await getDoc(doc(db, 'users', user.uid)) : null;
        const roles = userDoc?.exists() ? (userDoc.data().roles || []) : [];
        const isStaffUser = roles.includes('admin') || roles.includes('coach') || roles.includes('assistant_coach');
        const [classSnap, athleteSnap, shirtSnap, attendanceSnap, planSnap, creditSnap] = await Promise.all([
          getDocs(collection(db, 'classes')),
          getDocs(collection(db, 'athletes')),
          isStaffUser ? getDocs(query(collection(db, 'shirtTasks'), where('done', '==', false))) : Promise.resolve({ docs: [] }),
          // One read of the whole ledger instead of a query per class. Same
          // documents either way — Firestore bills per document — but 1 round
          // trip rather than one for every class on the page.
          getDocs(collection(db, 'attendance')),
          getDocs(collection(db, 'plans')),
          // Staff only, and deliberately. The rule authorises a credit read by
          // ownership, and a query filtered on status alone cannot prove that
          // — Firestore rejects the whole query rather than filtering it, which
          // would take the check-in page down for every parent.
          //
          // Parents lose nothing they could have used: a parent over the weekly
          // allowance is stopped and told to ask a coach, so they were never
          // going to spend a credit from here anyway.
          isStaffUser
            ? getDocs(query(collection(db, 'makeupCredits'), where('status', '==', 'available')))
            : Promise.resolve({ docs: [] }),
        ]);
        setShirtAthleteIds(new Set(shirtSnap.docs.map(d => d.data().athleteId).filter(Boolean)));

        const isAdminOnly = roles.includes('admin');
        const coachRole = (roles.includes('coach') || roles.includes('assistant_coach')) && !isAdminOnly;
        const parentOrAthlete = !isAdminOnly && !coachRole;
        setIsParentOrAthlete(parentOrAthlete);
        setIsCoach(coachRole);
        setIsAdmin(isAdminOnly);

        // If coach, fetch their coach record and today's assigned classes
        if (coachRole && user) {
          const coachSnap = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
          if (!coachSnap.empty) {
            const coach = { id: coachSnap.docs[0].id, ...coachSnap.docs[0].data() };
            setCoachData(coach);
          }
        }

        const allClasses = classSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Load all coaches for name display on class cards
        const coachListSnap = await getDocs(query(collection(db, 'users'), where('roles', 'array-contains-any', ['coach', 'assistant_coach'])));
        const coachList = coachListSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllCoaches(coachList);

        let filtered;
        if (coachRole) {
          // Coaches: only today's classes assigned to them
          const coachSnap2 = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
          const coachId = !coachSnap2.empty ? coachSnap2.docs[0].id : user.uid;
          filtered = allClasses
            .filter(c => c.date === today && c.status !== 'canceled' &&
              (c.headCoachId === coachId || (c.assistantCoachIds || []).includes(coachId)))
            .sort((a, b) => a.startTime.localeCompare(b.startTime));
        } else if (parentOrAthlete) {
          // Parents/athletes: today's classes (check in) + the next two weeks
          // (reserve). Block time is facility-internal and never shown to players.
          const twoWeeksOut = dateStr(new Date(Date.now() + 14 * 86400000));
          filtered = allClasses
            .filter(c =>
              c.date >= today && c.date <= twoWeeksOut &&
              c.status !== 'canceled' &&
              c.sessionType !== 'block-time' &&
              c.sessionType !== 'league'
            )
            .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
        } else {
          // Admins: all classes (past, present, future) so filters work
          filtered = allClasses
            .filter(c => c.status !== 'canceled')
            .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
        }

        setClasses(filtered);

        // Class counts and per-athlete activity, both derived from the single
        // attendance read above. This used to be one getDocs per visible class:
        // 550 queries for an admin, to count records a single query returns.
        const attendanceRows = attendanceSnap.docs.map(d => d.data());
        setAllAttendance(attendanceRows);
        setPlansById(Object.fromEntries(planSnap.docs.map(d => [d.id, d.data()])));
        setMakeupCredits(creditSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        const countsMap = {};
        const activityMap = {};
        const activeSince = dateStr(new Date(Date.now() - ACTIVITY_WINDOW_DAYS * 86400000));
        for (const r of attendanceRows) {
          // Coaches check in to the same collection, with type: 'coach' and no
          // athleteId, because that is how their hours are recorded. They are
          // not in the class. Counting them made 46 of 92 classes read one to
          // three heads fuller than they were, and this count decides the card
          // colour and "N spots left" — so a session with two coaches on it
          // would have shown Full with two places free, the moment any class
          // set a maxAthletes.
          if (r.classId && r.type !== 'coach') {
            countsMap[r.classId] = (countsMap[r.classId] || 0) + 1;
          }
          // A reservation is an intention, not a visit — only attendance counts
          // toward "most active". Legacy rows predate the status field.
          if (r.athleteId && r.status !== 'reserved' && (r.date || '') >= activeSince) {
            activityMap[r.athleteId] = (activityMap[r.athleteId] || 0) + 1;
          }
        }
        setClassCounts(countsMap);
        setAthleteActivity(activityMap);

        const allAthletes = athleteSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.athleteType !== 'unknown')
          .filter(a => !a.isArchived);

        if (parentOrAthlete && user) {
          // Parents: only their own adult athlete record + linked juniors.
          // Junior linkage supports both legacy parentUserId and the new
          // parentUserIds array (added to support co-parents).
          const ownAthletes = allAthletes.filter(a => a.email === user.email);
          const linkedJuniors = allAthletes.filter(a =>
            a.parentUserId === user.uid ||
            (Array.isArray(a.parentUserIds) && a.parentUserIds.includes(user.uid))
          );
          let myAthletes = [...ownAthletes, ...linkedJuniors];

          // New adult with no athlete record yet — create a virtual self-athlete
          if (myAthletes.length === 0) {
            const userDocSnap = await getDoc(doc(db, 'users', user.uid));
            const userData = userDocSnap.exists() ? userDocSnap.data() : {};
            myAthletes = [{
              id: user.uid,
              athleteId: user.uid,
              firstName: userData.firstName || user.displayName?.split(' ')[0] || '',
              lastName: userData.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
              email: user.email,
              athleteType: 'adult',
              sessionBalance: 0,
              _isSelf: true,
            }];
          }
          setAthletes(myAthletes);

          // Which classes has this family already reserved / joined? Drives
          // the green (reserved) tile highlighting below.
          try {
            const myIds = myAthletes.map(a => a.id).filter(Boolean);
            const statusMap = {};
            for (let i = 0; i < myIds.length; i += 10) {
              const chunk = myIds.slice(i, i + 10);
              const attSnap = await getDocs(query(collection(db, 'attendance'), where('athleteId', 'in', chunk)));
              attSnap.docs.forEach(d => {
                const a = d.data();
                if (!a.classId || a.type === 'coach') return;
                const st = a.status || 'checked_in';
                if (st === 'reserved' || !statusMap[a.classId]) statusMap[a.classId] = st;
              });
            }
            setMyClassStatus(statusMap);
          } catch (e) { console.warn('my-reservation lookup failed:', e); }
          // Auto-select if only one athlete
          if (myAthletes.length === 1) setSelectedAthlete(myAthletes[0]);
        } else {
          setAthletes(allAthletes);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error in fetchData:', err);
        throw err; // Re-throw to be caught by outer catch
      }
    };
    fetchData().catch(err => {
      console.error('CheckIn fetchData error:', err);
      logError('CheckIn', err, { function: 'fetchData', userId: auth.currentUser?.uid });
      setError(`Failed to load schedule: ${err.message || 'Please refresh the page and try again.'}`);
      setLoading(false);
    });
  }, []);

  const loadAttendance = async (classId) => {
    setSelectedClass(classId);
    setAttendance([]);
    const snap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', classId)));
    const records = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setAttendance(records);
    setClassCounts(prev => ({ ...prev, [classId]: records.length }));
  };

  // Helper function to determine if a sessionType is for adults or juniors
  const getSessionAgeGroup = (sessionType) => {
    const type = (sessionType || '').toLowerCase();
    if (type.includes('junior') || type.includes('kids') || type.includes('youth')) {
      return 'junior';
    }
    if (type.includes('adult') || type.includes('open')) {
      return 'adult';
    }
    // Default to adult for unknown session types to be safe
    return 'adult';
  };

  // Enhanced check-in validation
  const validateAthleteForClass = (athlete, classData) => {
    const athleteType = (athlete.athleteType || '').toLowerCase();
    const sessionAgeGroup = getSessionAgeGroup(classData.sessionType);
    
    if (athleteType === 'junior' && sessionAgeGroup === 'adult') {
      return { valid: false, reason: 'Junior athletes cannot check into adult classes' };
    }
    
    if (athleteType === 'adult' && sessionAgeGroup === 'junior') {
      return { valid: false, reason: 'Adult athletes cannot check into junior classes' };
    }
    
    return { valid: true };
  };

  // Legacy attendance docs have no status field — treat missing status as checked in.
  const isCheckedIn = (athleteId) => attendance.some(a => a.athleteId === athleteId && a.status !== 'reserved' && a.status !== 'waitlisted');
  const getReservation = (athleteId) => attendance.find(a => a.athleteId === athleteId && a.status === 'reserved');
  // Athlete records carry gender as 'Male'/'Female'; older ones used athleteGender.
  const genderOf = (a) => String(a.gender || a.athleteGender || '').trim().toLowerCase();
  const getWaitlist = (athleteId) => attendance.find(a => a.athleteId === athleteId && a.status === 'waitlisted');
  // A "held" record (reserved or waitlisted) can be converted to a check-in.
  const getHeld = (athleteId) => attendance.find(a => a.athleteId === athleteId && (a.status === 'reserved' || a.status === 'waitlisted'));
  const sortByWaitTime = (a, b) => {
    const t = r => r.waitlistedAt?.seconds ?? (r.waitlistedAt ? new Date(r.waitlistedAt).getTime() / 1000 : 0);
    return t(a) - t(b);
  };

  // When a spot frees up (cancel / undo), promote the earliest-waitlisted
  // athlete to reserved and email them. Fail-soft on the email.
  const promoteFromWaitlist = async (classId) => {
    try {
      const classData = classes.find(c => c.id === classId);
      if (!classData || !classData.maxAthletes) return;
      const snap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', classId)));
      const recs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const active = recs.filter(r => r.type !== 'coach' && r.status !== 'waitlisted').length;
      if (active >= classData.maxAthletes) return;
      const waiting = recs.filter(r => r.status === 'waitlisted').sort(sortByWaitTime);
      if (waiting.length === 0) return;
      const next = waiting[0];
      await updateDoc(doc(db, 'attendance', next.id), {
        status: 'reserved',
        reservedAt: new Date(),
        promotedFromWaitlist: true,
      });
      if (classId === selectedClass) {
        setAttendance(prev => prev.map(x => x.id === next.id ? { ...x, status: 'reserved', promotedFromWaitlist: true } : x));
      }
      logEvent('waitlist.promoted', {
        target: { id: next.athleteId || '', email: next.athleteEmail || '', name: next.athleteName || '' },
        description: `Promoted from wait-list to reserved in ${classData.eventName || classData.sessionType || 'class'} (${classData.date || ''})`,
        metadata: { classId },
      });
      try {
        const functions = getFunctions();
        const sendPromo = httpsCallable(functions, 'sendWaitlistPromotion');
        await sendPromo({
          athleteName: next.athleteName || '',
          athleteEmail: next.athleteEmail || '',
          eventName: classData.eventName || classData.sessionType || 'class',
          date: classData.date || '',
          startTime: classData.startTime || '',
        });
      } catch (e) { console.warn('Waitlist promotion email failed:', e); }
    } catch (e) { console.error('promoteFromWaitlist failed:', e); }
  };
  const localToday = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  };

  // Reserve a spot in a future class. No session is deducted — that happens at
  // check-in on class day. Capacity-checked against maxAthletes (null = no cap).
  const handleReserve = async (athlete) => {
    if (!selectedClass) return;
    if (attendance.some(a => a.athleteId === athlete.id)) return; // already reserved or checked in
    const classData = classes.find(c => c.id === selectedClass);
    if (!classData) { setError('Class not found'); return; }
    const validation = validateAthleteForClass(athlete, classData);
    if (!validation.valid) { setError(validation.reason); return; }
    const classFullNow = !!(classData.maxAthletes &&
      attendance.filter(a => a.status !== 'waitlisted').length >= classData.maxAthletes);
    setCheckingIn(athlete.id);
    setError('');
    try {
      const newStatus = classFullNow ? 'waitlisted' : 'reserved';
      await addDoc(collection(db, 'attendance'), {
        classStartsAt: classStartsAt(classData),
        classId: selectedClass,
        athleteId: athlete.id,
        athleteName: `${athlete.firstName} ${athlete.lastName}`,
        athleteEmail: athlete.email || '',
        date: classData?.date || '',
        sessionType: classData?.sessionType || '',
        status: newStatus,
        ...(classFullNow ? { waitlistedAt: new Date() } : { reservedAt: new Date() }),
      });
      const freshSnap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', selectedClass)));
      setAttendance(freshSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setClassCounts(prev => ({ ...prev, [selectedClass]: freshSnap.size }));
      setSuccess(classFullNow
        ? `${athlete.firstName} ${athlete.lastName} joined the wait-list. We'll email if a spot opens.`
        : `${athlete.firstName} ${athlete.lastName} reserved a spot. Session will be deducted at check-in.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('CheckIn', err, { function: 'handleReserve', athleteId: athlete.id });
      setError('Failed to reserve. Please try again.');
    } finally {
      setCheckingIn(null);
    }
  };

  // Cancel a reservation (any time, no cutoff). Nothing to refund — no session
  // was deducted at reserve time.
  const handleCancelReservation = async (athlete) => {
    const reservation = getHeld(athlete.id);
    if (!reservation || !reservation.id) return;
    setCheckingIn(athlete.id);
    setError('');
    try {
      await deleteDoc(doc(db, 'attendance', reservation.id));
      logEvent(reservation.status === 'waitlisted' ? 'waitlist.left' : 'reservation.canceled', {
        target: { id: athlete.id || '', email: athlete.email || '', name: `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() },
        description: `${reservation.status === 'waitlisted' ? 'Left the wait-list' : 'Canceled a reservation'} for ${reservation.sessionType || 'class'} (${reservation.date || ''})`,
        metadata: { classId: selectedClass || '' },
      });
      if (reservation.status === 'reserved') await promoteFromWaitlist(selectedClass);
      const freshSnap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', selectedClass)));
      setAttendance(freshSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setClassCounts(prev => ({ ...prev, [selectedClass]: freshSnap.size }));
      setSuccess(reservation.status === 'waitlisted'
        ? `${athlete.firstName} ${athlete.lastName} removed from the wait-list.`
        : `Reservation canceled for ${athlete.firstName} ${athlete.lastName}.`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      logError('CheckIn', err, { function: 'handleCancelReservation', athleteId: athlete.id });
      setError('Failed to cancel reservation. Please try again.');
    } finally {
      setCheckingIn(null);
    }
  };

  // `force` is the coach answering the over-allowance dialog. Nothing else
  // passes it, so the warning cannot be skipped by accident.
  const handleCheckIn = async (athlete, force = false) => {
    if (isCheckedIn(athlete.id)) return;
    if (!selectedClass) return;
    
    const classData = classes.find(c => c.id === selectedClass);
    if (!classData) {
      setError('Class not found');
      return;
    }
    if (classData.date > localToday()) {
      setError('This class is in the future \u2014 reserve a spot instead, then check in on class day.');
      return;
    }
    // Capacity: members can't walk into a full class (their reserved spot, if
    // any, already counts). Admins and coaches can override.
    if (!(isAdmin || isCoach) && classData.maxAthletes) {
      const holders = attendance.filter(a => a.status !== 'waitlisted');
      const mine = holders.some(a => a.athleteId === athlete.id);
      if (!mine && holders.length >= classData.maxAthletes) {
        setError('This class is full.');
        return;
      }
    }

    // Validate athlete type matches class type
    const validation = validateAthleteForClass(athlete, classData);
    if (!validation.valid) {
      setError(validation.reason);
      logError('CheckIn', new Error('Athlete type mismatch'), {
        athleteId: athlete.id,
        athleteType: athlete.athleteType,
        sessionType: classData.sessionType,
        classId: selectedClass,
        reason: validation.reason
      });
      return;
    }

    // Block self check-in with zero/negative balance — only coaches and admins can do this
    if (isParentOrAthlete && !isComped(athlete) && (athlete.sessionBalance || 0) <= 0) {
      setError('You have no sessions remaining. Please purchase more, or ask a coach or admin to check you in.');
      return;
    }

    // Weekly allowance. The recurring plans sell a rate — one practice a week,
    // or two — and sessionBalance cannot express that: it is a monthly pot, so
    // it would happily let a 1x athlete take all four in one week.
    //
    // A coach is warned, not blocked. The club's standing choice is that
    // billing does not turn a kid away from the sand, and this keeps to it: the
    // dialog says what is happening and the coach decides. A parent checking in
    // their own child is stopped, the same way a zero balance stops them.
    const cap = checkWeeklyCap({
      plan: plansById[athlete.activePlanId],
      attendance: allAttendance,
      athleteId: athlete.id,
      dateStr: classData?.date || localToday(),
      makeupCredits: makeupCredits.filter(c => c.athleteId === athlete.id),
    });
    if (cap.applies && !cap.allowed && !isComped(athlete) && !force) {
      if (isParentOrAthlete) {
        setError(`${athlete.firstName} has already had their ${cap.allowance} practice`
          + `${cap.allowance === 1 ? '' : 's'} this week. Ask a coach if you need another.`);
        return;
      }
      setCapWarning({ athlete, cap });
      return;
    }

    setCheckingIn(athlete.id);
    setError('');
    try {
      const currentBalance = athlete.sessionBalance || 0;
      // Comped athletes keep their balance; everyone else spends one. Negative
      // is still allowed for the rest, to track attendance beyond what's paid.
      const newBalance = balanceAfterCheckIn(athlete);

      const existingReservation = getHeld(athlete.id);
      if (existingReservation && existingReservation.id) {
        // Convert the held spot into a check-in (deducting the session now).
        await updateDoc(doc(db, 'attendance', existingReservation.id), {
          status: 'checked_in',
          checkedInAt: new Date(),
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
        });
      } else {
        await addDoc(collection(db, 'attendance'), {
          classStartsAt: classStartsAt(classData),
          classId: selectedClass,
          athleteId: athlete.id,
          athleteName: `${athlete.firstName} ${athlete.lastName}`,
          athleteEmail: athlete.email || '', // Add email for ownership check in Firestore rules
          date: classData?.date || '',
          sessionType: classData?.sessionType || '',
          status: 'checked_in',
          checkedInAt: new Date(),
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
        });
      }

      // A credit is spent only when the week's allowance could not cover this
      // session. Marked used rather than deleted: the ledger is the record of
      // what the club owed and settled, and a deleted row answers nothing.
      if (cap.usesMakeup) {
        const credit = nextMakeupCredit(makeupCredits.filter(c => c.athleteId === athlete.id));
        if (credit) {
          await updateDoc(doc(db, 'makeupCredits', credit.id), {
            status: 'used', usedAt: new Date(), usedForClassId: selectedClass,
          });
          setMakeupCredits(prev => prev.filter(c => c.id !== credit.id));
        }
      }

      // Update athlete balance if not a virtual self-athlete
      if (!athlete._isSelf) {
        await updateDoc(doc(db, 'athletes', athlete.id), {
          // The cost, not the computed total: two people checking the same
          // athlete in at once would otherwise each write their own idea of
          // the balance and one session would vanish.
          sessionBalance: increment(-sessionCost(athlete)),
          lastCheckIn: serverTimestamp(),
        });
      }

      setAthletes(prev => prev.map(a => a.id === athlete.id ? { ...a, sessionBalance: newBalance } : a));
      // Reload full attendance so undo has real Firestore doc IDs
      const freshSnap = await getDocs(query(collection(db, 'attendance'), where('classId', '==', selectedClass)));
      setAttendance(freshSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setClassCounts(prev => ({ ...prev, [selectedClass]: freshSnap.size }));
      setSuccess(
        newBalance < 0 
          ? `${athlete.firstName} ${athlete.lastName} checked in. Balance: ${newBalance} sessions (negative balance tracked).`
          : `${athlete.firstName} ${athlete.lastName} checked in. Balance: ${newBalance} sessions.`
      );
      setTimeout(() => setSuccess(''), 4000);

      // Send low balance alert if balance hits 2 or below
      if (newBalance <= 2) {
        try {
          const functions = getFunctions();
          const checkBalance = httpsCallable(functions, 'checkBalanceAfterCheckin');
          await checkBalance({ athleteId: athlete.id });
        } catch (err) {
          logError('CheckIn', err.message || String(err), err.stack || '');
          console.error('Low balance alert failed:', err.code, err.message, err);
        }
      }

      // If coach (not admin) checks in a zero or negative balance athlete, alert admin
      if (isCoach && !isAdmin && currentBalance <= 0) {
        try {
          const functions = getFunctions();
          const alertAdmin = httpsCallable(functions, 'alertNegativeBalance');
          await alertAdmin({
            athleteId: athlete.id,
            athleteName: `${athlete.firstName} ${athlete.lastName}`,
            newBalance,
            classId: selectedClass,
            className: classData?.eventName || classData?.sessionType || '',
          });
        } catch (err) {
          console.error('Negative balance alert failed:', err);
        }
      }
    } catch (err) {
      logError('CheckIn', err.message || String(err), err.stack || '');
      setError('Check-in failed. Please try again.');
    }
    setCheckingIn(null);
  };

  const handleUndo = async (athlete) => {
    const classData = classes.find(c => c.id === selectedClass);
    
    // Check permissions for undo
    const canUndo = isCoach || isAdmin || (() => {
      // Players can undo their own check-ins until class starts
      if (isParentOrAthlete) {
        const user = auth.currentUser;
        if (!user || !classData) return false;
        
        // Check if this is the player's own check-in. Supports both legacy
        // parentUserId and new parentUserIds array (co-parent scenario).
        const isOwnCheckIn = athlete.email === user.email ||
                           athlete.parentUserId === user.uid ||
                           (Array.isArray(athlete.parentUserIds) && athlete.parentUserIds.includes(user.uid)) ||
                           athlete.id === user.uid;
        
        if (!isOwnCheckIn) return false;
        
        // Check if class hasn't started yet
        const now = new Date();
        const classDateTime = new Date(`${classData.date}T${classData.startTime}`);
        
        return now < classDateTime; // Allow undo before class starts
      }
      return false;
    })();
    
    if (!canUndo) {
      const classData = classes.find(c => c.id === selectedClass);
      if (classData) {
        const now = new Date();
        const classDateTime = new Date(`${classData.date}T${classData.startTime}`);
        const hasStarted = now >= classDateTime;
        
        if (hasStarted) {
          setError('Cannot undo check-in after class has started. Contact a coach or admin for assistance.');
        } else {
          setError('You can only undo your own check-ins before class starts.');
        }
      } else {
        setError('Only coaches and admins can undo check-ins.');
      }
      return;
    }
    
    setError('');
    try {
      const attendanceRecord = attendance.find(a => a.athleteId === athlete.id);
      if (!attendanceRecord || !attendanceRecord.id) {
        setError('Could not find check-in record. Please refresh and try again.');
        return;
      }

      // Restore to balanceBefore stored at check-in time — avoids giving free sessions to 0-balance athletes
      // Give back exactly what the check-in took. The row records the balance
      // either side of it, so the difference is the true cost — one session
      // normally, none for a comped athlete.
      //
      // Restoring to balanceBefore, as this did, was wrong beyond the race: if
      // the athlete had since checked into another class, resetting to the
      // earlier snapshot refunded that session too.
      const refund = (attendanceRecord.balanceBefore !== undefined
        && attendanceRecord.balanceAfter !== undefined)
        ? attendanceRecord.balanceBefore - attendanceRecord.balanceAfter
        : sessionCost(athlete);   // rows written before balanceAfter was recorded
      const restoredBalance = (athlete.sessionBalance || 0) + refund;   // on-screen only

      // Update athlete balance if not a virtual self-athlete
      if (!athlete._isSelf && refund !== 0) {
        await updateDoc(doc(db, 'athletes', athlete.id), { sessionBalance: increment(refund) });
      }
      await deleteDoc(doc(db, 'attendance', attendanceRecord.id));

      setAthletes(prev => prev.map(a => a.id === athlete.id ? { ...a, sessionBalance: restoredBalance } : a));
      setAttendance(prev => prev.filter(a => a.athleteId !== athlete.id));
      const undoClassId = attendanceRecord.classId || selectedClass;
      if (undoClassId) setClassCounts(prev => ({ ...prev, [undoClassId]: Math.max(0, (prev[undoClassId] || 1) - 1) }));
      logEvent('checkin.undone', {
        target: { id: athlete.id || '', email: athlete.email || '', name: `${athlete.firstName || ''} ${athlete.lastName || ''}`.trim() },
        description: `Check-in undone for ${attendanceRecord.sessionType || 'class'} (${attendanceRecord.date || ''})`,
        metadata: { classId: undoClassId || '' },
      });
      if (undoClassId) await promoteFromWaitlist(undoClassId);
      setSuccess(`Check-in undone for ${athlete.firstName} ${athlete.lastName}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logError('CheckIn', err, { function: 'handleUndo', athleteId: athlete.id, attendanceId: attendance.find(a => a.athleteId === athlete.id)?.id });
      console.error('Undo failed:', err.code, err.message, err);
      
      // Provide more specific error messages
      if (err.code === 'permission-denied') {
        setError('Permission denied: Unable to undo check-in. Please contact an admin or check your permissions.');
      } else if (err.code === 'not-found') {
        setError('Attendance record not found. It may have already been deleted. Please refresh the page.');
      } else {
        setError(`Failed to undo check-in: ${err.message || err.code || 'Unknown error'}`);
      }
    }
  };

  const selectedClassData = classes.find(c => c.id === selectedClass);
  const adultSession = !!selectedClassData && getSessionAgeGroup(selectedClassData.sessionType) === 'adult';
  // Coaches check in against the same collection but have no status, so they
  // passed both tests and were counted as athletes — the header read "13
  // athletes checked in" over a list of 11. Every other count in the app
  // excludes them the same way (see line ~277, Reports and People).
  const checkedInCount = attendance.filter(a =>
    a.type !== 'coach' && a.status !== 'reserved' && a.status !== 'waitlisted').length;

  // Admin multi-select filter logic
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

  const matchesFilter = (c, filter) => {
    const d = c.date || '';
    const st = (c.sessionType || '').toLowerCase();
    const sg = (c.skillGroup || '').toLowerCase();

    // Compute week boundaries (Monday–Sunday)
    const getWeekRange = (offset = 0) => {
      const base = new Date(now);
      const day = base.getDay(); // 0=Sun
      const diffToMon = (day === 0 ? -6 : 1 - day) + offset * 7;
      const mon = new Date(base); mon.setDate(base.getDate() + diffToMon); mon.setHours(0,0,0,0);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      return { start: fmt(mon), end: fmt(sun) };
    };

    if (filter === 'past')         return d < todayStr;
    if (filter === 'today')        return d === todayStr;
    if (filter === 'future')       return d > todayStr;
    if (filter === 'lastweek')     { const { start, end } = getWeekRange(-1); return d >= start && d <= end; }
    if (filter === 'thisweek')     { const { start, end } = getWeekRange(0);  return d >= start && d <= end; }
    if (filter === 'nextweek')     { const { start, end } = getWeekRange(1);  return d >= start && d <= end; }
    if (filter === 'junior')       return st.startsWith('junior');
    if (filter === 'beginner')     return st.startsWith('junior') && sg === 'beginner';
    if (filter === 'intermediate') return st.startsWith('junior') && sg === 'intermediate';
    if (filter === 'adult')        return st.startsWith('adult');
    if (filter === 'block')        return st === 'block-time';
    return true;
  };

  const DATE_FILTERS = ['past', 'lastweek', 'thisweek', 'today', 'nextweek', 'future'];
  const TYPE_FILTERS = ['junior', 'beginner', 'intermediate', 'adult', 'block'];

  const activeDate = classFilters.filter(f => DATE_FILTERS.includes(f));
  const activeType = classFilters.filter(f => TYPE_FILTERS.includes(f));

  const adminFilteredClasses = isAdmin
    ? classes.filter(c => {
        const dateOk = activeDate.length === 0 || activeDate.some(f => matchesFilter(c, f));
        const typeOk = activeType.length === 0 || activeType.some(f => matchesFilter(c, f));
        return dateOk && typeOk;
      })
    : classes;

  // For parent/athlete mode: filter classes by selected athlete type (or show all if no athlete selected yet)
  const visibleClasses = isParentOrAthlete
    ? selectedAthlete 
      ? classes.filter(c => {
          if (c.status === 'canceled') return false;
          
          // Filter by athlete type vs class type using the helper function
          const athleteType = (selectedAthlete.athleteType || '').toLowerCase();
          const sessionAgeGroup = getSessionAgeGroup(c.sessionType);
          
          if (athleteType === 'junior' && sessionAgeGroup === 'adult') return false;
          if (athleteType === 'adult' && sessionAgeGroup === 'junior') return false;
          return true;
        })
      : classes.filter(c => c.status !== 'canceled') // Show all non-canceled classes if no athlete selected
    : isAdmin ? adminFilteredClasses : classes;

  const filteredAthletes = (isParentOrAthlete && selectedAthlete ? [selectedAthlete] : athletes).filter(a => {
    const name = `${a.firstName} ${a.lastName}`.toLowerCase();
    if (!name.includes(search.toLowerCase())) return false;
    
    // Filter by class type and skill group if a class is selected
    if (selectedClassData) {
      const athleteType = (a.athleteType || '').toLowerCase();
      const sessionAgeGroup = getSessionAgeGroup(selectedClassData.sessionType);
      
      // Strict athlete type matching — only show athletes whose type matches the class
      if (sessionAgeGroup === 'adult' && athleteType !== 'adult') return false;
      if (sessionAgeGroup === 'junior' && athleteType !== 'junior') return false;
    }

    // The last three dropdown choices narrow the list instead of reordering it.
    if (athleteSort === 'checkedin' && !isCheckedIn(a.id)) return false;
    if (athleteSort === 'boys' && genderOf(a) !== 'male') return false;
    if (athleteSort === 'girls' && genderOf(a) !== 'female') return false;
    return true;
  })
    .sort((a, b) => {
      const nameOf = (x) => `${x.firstName || ''} ${x.lastName || ''}`.trim().toLowerCase();
      if (athleteSort === 'active') {
        const diff = (athleteActivity[b.id] || 0) - (athleteActivity[a.id] || 0);
        if (diff) return diff;      // ties fall back to alphabetical
      }
      return nameOf(a).localeCompare(nameOf(b));
    });

  const formatSessionType = (t) => {
    const map = {
      'junior-practice': 'Junior Practice',
      'junior-private': 'Junior Private',
      'junior-drop-in': 'Junior Drop-in',
      'junior-evaluation': 'Junior Evaluation',
      'adult-practice': 'Adult Practice',
      'league': 'League',
    };
    return map[t?.toLowerCase()] || (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !selectedClass) return;
    setMsgSending(true);
    try {
      const cls = classes.find(c => c.id === selectedClass);
      const coachName = `${coachData?.firstName || ''} ${coachData?.lastName || ''}`.trim();
      const classInfo = cls ? `${formatSessionType(cls.sessionType)}${cls.skillGroup ? ' · ' + cls.skillGroup : ''} — ${formatTime(cls.startTime)}${cls.courtNumber ? ', Court ' + cls.courtNumber : ''}` : '';

      // Who is actually in THIS class: the attendance rows loaded for it, minus
      // the coach's own. `athletes` is every athlete in the club in coach mode
      // (setAthletes(allAthletes) above), so using it here addressed the whole
      // club rather than the roster — harmless only because the parent lookup
      // below never matched anybody, and a mass text the moment it did.
      const rosterAthleteIds = new Set(
        attendance.filter(r => r.type !== 'coach').map(r => r.athleteId).filter(Boolean));
      if (rosterAthleteIds.size === 0) {
        setMsgSent(true);
        setMsgSending(false);
        return;
      }
      const rosterAthletes = athletes.filter(a => rosterAthleteIds.has(a.id));

      // Parents come off the athlete docs, not off users.linkedAthletes. That
      // field is written [] at registration and nothing has ever added to it —
      // empty on all 63 parent accounts — so every class message sent to a
      // parent has gone nowhere since the feature was written. Every other
      // screen resolves a parent through parentUserId / parentUserIds, and now
      // so does this.
      const parentUids = new Set();
      rosterAthletes.forEach(a => getAllParentUids(a).forEach(uid => parentUids.add(uid)));
      // An adult athlete's own account, matched the two ways the link is
      // actually stored — athletes.userId, and email — rather than by assuming
      // the athlete doc id is the uid, which it has not been since athlete ids
      // became ath_<uid>_<timestamp>.
      const selfUserIds = new Set(rosterAthletes.map(a => a.userId).filter(Boolean));
      const selfEmails = new Set(
        rosterAthletes.map(a => (a.email || '').toLowerCase()).filter(Boolean));

      const userSnap = await getDocs(collection(db, 'users'));
      const tokens = [];
      userSnap.forEach(doc => {
        const data = doc.data();
        if (!data.fcmTokens?.length) return;
        if (parentUids.has(doc.id)) { tokens.push(...data.fcmTokens); return; }
        if (selfUserIds.has(doc.id) || rosterAthleteIds.has(doc.id)
          || (data.email && selfEmails.has(data.email.toLowerCase()))) {
          tokens.push(...data.fcmTokens);
        }
      });

      if (tokens.length > 0) {
        const functions = getFunctions();
        const send = httpsCallable(functions, 'sendPushNotification');
        await send({
          tokens,
          title: `Message from ${coachName} 🏐`,
          body: `${classInfo ? classInfo + ' - ' : ''}${msgText.trim()}`,
        });
      }

      setMsgSent(true);
    } catch (err) {
      logError('CheckIn', err.message || String(err), err.stack || '');
      console.error('Message failed:', err);
      setError('Failed to send message.');
    }
    setMsgSending(false);
  };

  const handleSendSubRequest = async () => {
    if (!subClass) return;
    setSubSending(true);
    try {
      const functions = getFunctions();
      const sendSub = httpsCallable(functions, 'sendSubRequest');
      await sendSub({
        coachName: `${coachData?.firstName || ''} ${coachData?.lastName || ''}`.trim(),
        classDate: subClass.date,
        startTime: subClass.startTime,
        endTime: subClass.endTime,
        courtNumber: subClass.courtNumber,
        sessionType: formatSessionType(subClass.sessionType),
        skillGroup: subClass.skillGroup || '',
        reason: subReason,
      });
      setSubSent(true);
    } catch (err) {
      logError('CheckIn', err.message || String(err), err.stack || '');
      console.error('Sub request failed:', err);
      setError('Failed to send sub request. Please try again.');
    }
    setSubSending(false);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
            Check-In
          </Typography>
          <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
            Select a class and check in athletes. Each check-in deducts one session from their balance.
          </Typography>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* ── Coach Single Page ── */}
        {!loading && isCoach && (
          <Box sx={{ mb: 3 }}>
            {/* Mode selector */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper onClick={() => setCoachMode('checkin')} elevation={coachMode === 'checkin' ? 4 : 1}
                sx={{ p: 2, borderRadius: 2, cursor: 'pointer', flex: '1 1 140px',
                  border: coachMode === 'checkin' ? '3px solid #1E3A5F' : '2px solid #ddd',
                  backgroundColor: coachMode === 'checkin' ? '#1E3A5F' : 'white',
                  textAlign: 'center', transition: 'all 0.15s' }}>
                <Typography variant="h5">✅</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: coachMode === 'checkin' ? 'white' : '#1E3A5F', mt: 0.5 }}>
                  Coach Check-In
                </Typography>
              </Paper>
              <Paper onClick={() => setCoachMode('sub')} elevation={coachMode === 'sub' ? 4 : 1}
                sx={{ p: 2, borderRadius: 2, cursor: 'pointer', flex: '1 1 140px',
                  border: coachMode === 'sub' ? '3px solid #E74C3C' : '2px solid #ddd',
                  backgroundColor: coachMode === 'sub' ? '#FDEDEC' : 'white',
                  textAlign: 'center', transition: 'all 0.15s' }}>
                <Typography variant="h5">🔁</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#E74C3C', mt: 0.5 }}>
                  Need a Sub
                </Typography>
              </Paper>
              <Paper onClick={() => { setMsgDialog(true); setMsgSent(false); setMsgText(''); }} elevation={1}
                sx={{ p: 2, borderRadius: 2, cursor: 'pointer', flex: '1 1 140px',
                  border: '2px solid #ddd', backgroundColor: 'white',
                  textAlign: 'center', transition: 'all 0.15s',
                  '&:hover': { backgroundColor: '#EAF4FB', borderColor: '#1A5276' } }}>
                <Typography variant="h5">💬</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1A5276', mt: 0.5 }}>
                  Message Class
                </Typography>
              </Paper>
            </Box>

            {/* Info note about negative balances */}
            {coachMode === 'checkin' && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#E3F2FD', borderRadius: 2, border: '1px solid #BBDEFB' }}>
                <Typography variant="body2" sx={{ color: '#1565C0', fontWeight: 600 }}>
                  💡 <strong>Note:</strong> You can check in athletes with zero or negative session balances. 
                  This helps track negative balances for athletes who attend without available sessions.
                </Typography>
              </Box>
            )}

            {/* Inline sub request form */}
            {coachMode === 'sub' && (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '2px solid #E74C3C', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#E74C3C', mb: 2 }}>🔁 Request a Sub</Typography>
                {subSent ? (
                  <Alert severity="success">Sub request sent! An admin has been notified.</Alert>
                ) : (
                  <>
                    <TextField fullWidth select label="Select Class" value={subClass?.id || ''} sx={{ mb: 2 }}
                      onChange={e => setSubClass(classes.find(c => c.id === e.target.value) || null)}>
                      {classes.length === 0
                        ? <MenuItem value="">No classes today</MenuItem>
                        : classes.map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            {formatSessionType(c.sessionType)}{c.skillGroup ? ` · ${c.skillGroup}` : ''} — {formatTime(c.startTime)}
                          </MenuItem>
                        ))
                      }
                    </TextField>
                    <TextField fullWidth multiline rows={3} label="Reason (optional)" value={subReason}
                      onChange={e => setSubReason(e.target.value)} placeholder="e.g. Sick, family emergency..." sx={{ mb: 2 }} />
                    <Button variant="contained" onClick={handleSendSubRequest} disabled={subSending || !subClass}
                      sx={{ backgroundColor: '#E74C3C', '&:hover': { backgroundColor: '#C0392B' } }}>
                      {subSending ? 'Sending...' : 'Send Sub Request'}
                    </Button>
                  </>
                )}
              </Paper>
            )}
          </Box>
        )}

        {/* ── Message Class Dialog ── */}
        {/* Over the weekly allowance. Says what the plan promised, what they
            have already had, and what proceeding means — then lets the coach
            proceed. A kid on the sand is not turned away over billing. */}
        <Dialog open={!!capWarning} onClose={() => setCapWarning(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Over their weekly practices</DialogTitle>
          <DialogContent>
            {capWarning && (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <strong>{capWarning.athlete.firstName} {capWarning.athlete.lastName}</strong> is on a
                  plan of {capWarning.cap.allowance} practice{capWarning.cap.allowance === 1 ? '' : 's'} a
                  week, and has already had {capWarning.cap.used} this week.
                </Typography>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  They have no makeup credits to cover it. Checking them in anyway is recorded,
                  so you can bill it or move them to a bigger plan later.
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCapWarning(null)}>Cancel</Button>
            <Button variant="contained" color="warning"
              onClick={() => { const w = capWarning; setCapWarning(null); handleCheckIn(w.athlete, true); }}>
              Check in anyway
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={msgDialog} onClose={() => { setMsgDialog(false); setMsgSent(false); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#1A5276', color: 'white' }}>Message Class</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {msgSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>Message sent to class successfully!</Alert>
            ) : (
              <>
                {selectedClass && (() => {
                  const cls = classes.find(c => c.id === selectedClass);
                  return cls ? (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f0f4f8', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>Sending to</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatSessionType(cls.sessionType)}{cls.skillGroup ? ` · ${cls.skillGroup}` : ''} — {formatTime(cls.startTime)}{cls.courtNumber ? `, Court ${cls.courtNumber}` : ''}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888' }}>{athletes.length} athlete{athletes.length !== 1 ? 's' : ''}</Typography>
                    </Box>
                  ) : null;
                })()}
                {!selectedClass && (
                  <Alert severity="warning" sx={{ mb: 2 }}>Please select a class first.</Alert>
                )}
                <TextField fullWidth multiline rows={4} label="Message *" value={msgText}
                  onChange={e => setMsgText(e.target.value)}
                  placeholder="e.g. Don't forget to bring water and sunscreen today!"
                  inputProps={{ maxLength: 200 }}
                  helperText={`${msgText.length}/200`} />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setMsgDialog(false); setMsgSent(false); }} sx={{ color: '#888' }}>
              {msgSent ? 'Close' : 'Cancel'}
            </Button>
            {!msgSent && (
              <Button variant="contained" onClick={handleSendMessage}
                disabled={msgSending || !msgText.trim() || !selectedClass}
                sx={{ backgroundColor: '#1A5276', '&:hover': { backgroundColor: '#1E3A5F' } }}>
                {msgSending ? 'Sending...' : 'Send Message'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* ── Sub Request Dialog ── */}
        <Dialog open={subDialog} onClose={() => { setSubDialog(false); setSubSent(false); setSubReason(''); setSubClass(null); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#E74C3C', color: 'white' }}>Request a Substitute</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {subSent ? (
              <Alert severity="success" sx={{ mt: 1 }}>Sub request sent to admin successfully!</Alert>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                  Select the class you need covered and add a reason.
                </Typography>
                <TextField fullWidth select label="Select Class" value={subClass?.id || ''} sx={{ mb: 2 }}
                  onChange={e => setSubClass(classes.find(c => c.id === e.target.value) || null)}>
                  {classes.length === 0
                    ? <MenuItem value="">No classes today</MenuItem>
                    : classes.map(c => (
                      <MenuItem key={c.id} value={c.id}>
                        {formatSessionType(c.sessionType)}{c.skillGroup ? ` · ${c.skillGroup}` : ''} — {formatTime(c.startTime)} Court {c.courtNumber}
                      </MenuItem>
                    ))
                  }
                </TextField>
                <TextField fullWidth multiline rows={3} label="Reason (optional)" value={subReason}
                  onChange={e => setSubReason(e.target.value)} placeholder="e.g. Sick, family emergency..." />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setSubDialog(false); setSubSent(false); setSubReason(''); setSubClass(null); }} sx={{ color: '#888' }}>
              {subSent ? 'Close' : 'Cancel'}
            </Button>
            {!subSent && (
              <Button variant="contained" onClick={handleSendSubRequest} disabled={subSending || !subClass}
                sx={{ backgroundColor: '#E74C3C', '&:hover': { backgroundColor: '#C0392B' } }}>
                {subSending ? 'Sending...' : 'Send Request'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Athlete Picker — parent/athlete mode only, when multiple athletes exist */}
            {isParentOrAthlete && !isCoach && athletes.length > 1 && (
              <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>
                  Who is checking in?
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {athletes.map(a => {
                    const isSelected = selectedAthlete?.id === a.id;
                    const isAdult = (a.athleteType || '').toLowerCase() === 'adult';
                    return (
                      <Paper key={a.id} onClick={() => { setSelectedAthlete(a); setSelectedClass(''); setAttendance([]); }}
                        elevation={isSelected ? 4 : 1}
                        sx={{
                          p: 2, borderRadius: 2, cursor: 'pointer', minWidth: 160,
                          border: isSelected ? '2px solid #1E3A5F' : '2px solid transparent',
                          backgroundColor: isSelected ? '#DEEAF1' : 'white',
                          display: 'flex', alignItems: 'center', gap: 1.5,
                        }}>
                        <Avatar sx={{ width: 36, height: 36, backgroundColor: isAdult ? '#C0392B' : '#2E6DA4', fontSize: '0.85rem' }}>
                          {a.firstName?.charAt(0)}{a.lastName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E3A5F' }}>
                            {a.firstName} {a.lastName}
                            {shirtAthleteIds.has(a.id) && (
                              <Box component="span" title="New junior — give shirt"
                                   sx={{ ml: 1, px: 0.75, py: 0.1, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
                                         backgroundColor: 'rgba(201,168,76,0.2)', color: '#8A6D0B' }}>
                                👕 shirt
                              </Box>
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#888', textTransform: 'capitalize' }}>
                            {isAdult ? 'Adult' : 'Junior'}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {/* Admin Class Filter Tiles */}
            {isAdmin && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#555', mb: 1.5 }}>Filter Classes</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { key: 'past',         label: 'Past Events',           color: '#7F8C8D' },
                    { key: 'lastweek',     label: 'Last Week',             color: '#95A5A6' },
                    { key: 'thisweek',     label: 'This Week',             color: '#2980B9' },
                    { key: 'today',        label: 'Today',                 color: '#1E3A5F' },
                    { key: 'nextweek',     label: 'Next Week',             color: '#1ABC9C' },
                    { key: 'future',       label: 'Future',                color: '#2E6DA4' },
                    { key: 'junior',       label: 'Junior',                color: '#8E44AD' },
                    { key: 'beginner',     label: 'Junior Beginner',       color: '#27AE60' },
                    { key: 'intermediate', label: 'Junior Intermediate',   color: '#E67E22' },
                    { key: 'adult',        label: 'Adult',                 color: '#C0392B' },
                    { key: 'block',        label: 'Block Time',            color: '#7F8C8D' },
                  ].map(f => (
                    <Box key={f.key} onClick={() => {
                        setClassFilters(prev => prev.includes(f.key) ? prev.filter(x => x !== f.key) : [...prev, f.key]);
                        setSelectedClass(''); setAttendance([]);
                      }}
                      sx={{
                        px: 2, py: 0.8, borderRadius: 2, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                        border: `2px solid ${classFilters.includes(f.key) ? f.color : '#ddd'}`,
                        backgroundColor: classFilters.includes(f.key) ? f.color : 'white',
                        color: classFilters.includes(f.key) ? 'white' : '#555',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: f.color, backgroundColor: classFilters.includes(f.key) ? f.color : `${f.color}15` },
                      }}>
                      {f.label}
                    </Box>
                  ))}
                </Box>

                {/* Info note about negative balances for admins */}
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#E8F5E8', borderRadius: 2, border: '1px solid #C8E6C9' }}>
                  <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 600 }}>
                    💡 <strong>Admin Note:</strong> You can check in athletes with zero or negative session balances. 
                    This helps track negative balances and attendance for athletes who attend without available sessions.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Class Selector — show after athlete selected (or always for admin/coach in checkin mode) */}
            {((!isParentOrAthlete) || athletes.length === 0 || athletes.length === 1 || selectedAthlete) && coachMode !== 'sub' && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>
                Select Class
              </Typography>
              {visibleClasses.length === 0 ? (
                <Alert severity="info">
                  {isParentOrAthlete 
                    ? selectedAthlete 
                      ? `No classes scheduled for ${selectedAthlete.athleteType || 'athlete'} today. Try selecting a different athlete or check tomorrow's schedule.`
                      : "No classes scheduled for today. Select an athlete above to see their available classes."
                    : "No classes scheduled for today."
                  }
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {visibleClasses.map(c => (
                    <Paper
                      key={c.id}
                      onClick={() => loadAttendance(c.id)}
                      elevation={selectedClass === c.id ? 4 : 1}
                      sx={(() => {
                        const count = classCounts[c.id] || 0;
                        const max = c.maxAthletes;
                        const pct = max ? count / max : 0;
                        let bg = '#E9F7EF'; // green - open
                        let border = '#27AE60';
                        if (max && pct >= 1) { bg = '#FDEDEC'; border = '#E74C3C'; } // red - full
                        else if (max && pct >= 0.75) { bg = '#FEF9E7'; border = '#E67E22'; } // orange - almost full
                        else if (max && pct >= 0.5) { bg = '#FFFDE7'; border = '#F9A825'; } // yellow - filling
                        if (isParentOrAthlete) {
                          // Player view: green = you're booked, neutral = everything
                          // else; full classes still read red.
                          const mine = myClassStatus[c.id];
                          bg = '#F4F8FB'; border = '#90A4AE';
                          if (max && pct >= 1) { bg = '#FDEDEC'; border = '#E74C3C'; }
                          if (mine === 'reserved' || mine === 'checked_in') { bg = '#E9F7EF'; border = '#27AE60'; }
                          else if (mine === 'waitlisted') { bg = '#F5EEF8'; border = '#7D3C98'; }
                        }
                        const isSelected = selectedClass === c.id;
                        if (isSelected) { bg = '#1E3A5F'; border = '#1E3A5F'; }
                        return { p: 2, borderRadius: 2, cursor: 'pointer', minWidth: 180,
                          boxShadow: isSelected ? '0 4px 20px rgba(30,58,95,0.4)' : undefined,
                          transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                          transition: 'all 0.15s ease',
                          border: `${isSelected ? '3px' : '2px'} solid ${border}`, backgroundColor: bg };
                      })()}
                    >
                      {selectedClass === c.id && (
                        <Typography variant="caption" sx={{ color: '#C9A84C', fontWeight: 800, display: 'block', letterSpacing: 1, fontSize: '0.7rem', mb: 0.5 }}>✓ SELECTED</Typography>
                      )}
                      {isParentOrAthlete && selectedClass !== c.id && (myClassStatus[c.id] === 'reserved' || myClassStatus[c.id] === 'checked_in') && (
                        <Typography variant="caption" sx={{ color: '#27AE60', fontWeight: 800, display: 'block', letterSpacing: 1, fontSize: '0.7rem', mb: 0.5 }}>
                          ✓ {myClassStatus[c.id] === 'checked_in' ? 'CHECKED IN' : 'RESERVED'}
                        </Typography>
                      )}
                      {isParentOrAthlete && selectedClass !== c.id && myClassStatus[c.id] === 'waitlisted' && (
                        <Typography variant="caption" sx={{ color: '#7D3C98', fontWeight: 800, display: 'block', letterSpacing: 1, fontSize: '0.7rem', mb: 0.5 }}>WAIT-LIST</Typography>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: selectedClass === c.id ? 'white' : '#1E3A5F', textTransform: 'capitalize' }}>
                        {formatSessionType(c.sessionType)}{c.skillGroup ? ` — ${c.skillGroup}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: selectedClass === c.id ? 'rgba(255,255,255,0.85)' : '#555' }}>
                        {formatDate(c.date)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: selectedClass === c.id ? 'rgba(255,255,255,0.8)' : '#888' }}>
                        {formatTime(c.startTime)} – {formatTime(c.endTime)}
                      </Typography>
                      {c.courtNumber && (
                        <Typography variant="caption" sx={{ display: 'block', color: selectedClass === c.id ? 'rgba(255,255,255,0.8)' : '#888' }}>
                          {c.courtNumber?.startsWith('court-') ? `Court ${c.courtNumber.replace('court-', '')}` : `Court ${c.courtNumber}`}
                        </Typography>
                      )}
                      {c.headCoachId && (() => {
                        const coach = allCoaches.find(co => co.id === c.headCoachId);
                        return coach ? (
                          <Typography variant="caption" sx={{ display: 'block', color: selectedClass === c.id ? 'rgba(255,255,255,0.8)' : '#555', mt: 0.25 }}>
                            {coach.firstName} {coach.lastName}
                          </Typography>
                        ) : null;
                      })()}
                      {(() => {
                        const count = classCounts[c.id] || 0;
                        const max = c.maxAthletes;
                        if (max) {
                          const spots = max - count;
                          const color = spots <= 0 ? '#E74C3C' : spots <= 2 ? '#E67E22' : '#27AE60';
                          return (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700, color }}>
                              {spots <= 0 ? 'Full' : `${spots} spot${spots !== 1 ? 's' : ''} left`}
                            </Typography>
                          );
                        }
                        // No limit set — show open spots as unlimited
                        return (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: selectedClass === c.id ? 'rgba(255,255,255,0.9)' : '#27AE60', fontWeight: 700 }}>
                            {count} checked in · Open
                          </Typography>
                        );
                      })()}
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
            )}

            {/* Athlete Check-In List */}
            {selectedClass && (
              <Paper elevation={2} sx={{ borderRadius: 3 }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
                      {formatSessionType(selectedClassData?.sessionType)} — {formatDate(selectedClassData?.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#888' }}>
                      {checkedInCount} athlete{checkedInCount !== 1 ? 's' : ''} checked in
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      placeholder="Search athletes..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      size="small"
                      sx={{ width: 240 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#888' }} /></InputAdornment>
                      }}
                    />
                    <TextField
                      select size="small" value={athleteSort}
                      onChange={e => setAthleteSort(e.target.value)}
                      sx={{ width: 170 }}
                      SelectProps={{ renderValue: v => (adultSession ? ADULT_SORT_LABELS : SORT_LABELS)[v] || SORT_LABELS.name }}
                    >
                      <MenuItem value="name">A–Z (name)</MenuItem>
                      <MenuItem value="active">Most active first</MenuItem>
                      <MenuItem value="checkedin">Checked in only</MenuItem>
                      <MenuItem value="boys">{adultSession ? 'Men only' : 'Boys only'}</MenuItem>
                      <MenuItem value="girls">{adultSession ? 'Women only' : 'Girls only'}</MenuItem>
                    </TextField>
                  </Box>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1E3A5F' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Athlete</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Sessions</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAthletes.map((athlete, i) => {
                        const checkedIn = isCheckedIn(athlete.id);
                        const reserved = !checkedIn && !!getReservation(athlete.id);
                        const waitlisted = !checkedIn && !reserved && !!getWaitlist(athlete.id);
                        const selClassForRow = classes.find(c => c.id === selectedClass);
                        const isFutureClass = !!(selClassForRow && selClassForRow.date > localToday());
                        const classFull = !!(selClassForRow && selClassForRow.maxAthletes &&
                          attendance.filter(x => x.status !== 'waitlisted').length >= selClassForRow.maxAthletes);
                        const waitPos = waitlisted
                          ? attendance.filter(x => x.status === 'waitlisted').sort(sortByWaitTime).findIndex(x => x.id === getWaitlist(athlete.id).id) + 1
                          : 0;
                        const isLoading = checkingIn === athlete.id;
                        const lowBalance = !checkedIn && athlete.sessionBalance <= 2 && athlete.sessionBalance > 0;
                        const noBalance = !checkedIn && athlete.sessionBalance <= 0;
                        return (
                          <TableRow
                            key={athlete.id}
                            sx={{
                              backgroundColor: checkedIn ? '#E9F7EF' : reserved ? '#FCF6E8' : waitlisted ? '#F5EEF8' : i % 2 === 0 ? '#f9f9f9' : 'white',
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar sx={{ width: 32, height: 32, backgroundColor: checkedIn ? '#27AE60' : '#2E6DA4', fontSize: '0.8rem' }}>
                                  {athlete.firstName?.charAt(0)}{athlete.lastName?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {athlete.firstName} {athlete.lastName}
                                    {shirtAthleteIds.has(athlete.id) && (
                                      <Box component="span" title="New junior — give shirt"
                                           sx={{ ml: 1, px: 0.75, py: 0.1, borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
                                                 backgroundColor: 'rgba(201,168,76,0.2)', color: '#8A6D0B' }}>
                                        👕 shirt
                                      </Box>
                                    )}
                                  </Typography>
                                  {athlete.schoolName && (
                                    <Typography variant="caption" sx={{ color: '#888' }}>{athlete.schoolName}</Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 'bold',
                                  color: noBalance ? '#E74C3C' : lowBalance ? '#E67E22' : '#27AE60'
                                }}
                              >
                                {isComped(athlete) ? 'Comp' : (athlete.sessionBalance || 0)}
                              </Typography>
                              {lowBalance && !noBalance && (
                                <Typography variant="caption" sx={{ color: '#E67E22', display: 'block' }}>Low</Typography>
                              )}
                              {noBalance && (
                                <Typography variant="caption" sx={{ color: '#E74C3C', display: 'block' }}>Empty</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {checkedIn ? (() => {
                                // Check if player can undo (before class starts and own check-in)
                                const user = auth.currentUser;
                                const classData = classes.find(c => c.id === selectedClass);
                                const canPlayerUndo = isParentOrAthlete && user && classData &&
                                  (athlete.email === user.email ||
                                   athlete.parentUserId === user.uid ||
                                   (Array.isArray(athlete.parentUserIds) && athlete.parentUserIds.includes(user.uid)) ||
                                   athlete.id === user.uid) &&
                                  new Date() < new Date(`${classData.date}T${classData.startTime}`);
                                
                                const canCoachAdminUndo = isCoach || isAdmin;
                                const canUndo = canPlayerUndo || canCoachAdminUndo;
                                
                                let buttonText = 'Undo';
                                let tooltip = '';
                                let buttonColor = '#1E3A5F';
                                
                                if (!canUndo && isParentOrAthlete) {
                                  if (classData && new Date() >= new Date(`${classData.date}T${classData.startTime}`)) {
                                    buttonText = 'Class Started';
                                    tooltip = 'Cannot undo after class has started. Contact a coach for assistance.';
                                    buttonColor = '#7F8C8D';
                                  } else {
                                    buttonText = 'Not Your Check-In';
                                    tooltip = 'You can only undo your own check-ins.';
                                    buttonColor = '#7F8C8D';
                                  }
                                }
                                
                                return (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleUndo(athlete)}
                                    disabled={!canUndo}
                                    title={tooltip}
                                    sx={{ 
                                      backgroundColor: buttonColor, 
                                      '&:hover': { backgroundColor: canUndo ? '#2E6DA4' : buttonColor },
                                      '&:disabled': { backgroundColor: buttonColor, color: 'white', opacity: 0.7 }
                                    }}
                                  >
                                    {buttonText}
                                  </Button>
                                );
                              })() : reserved ? (() => {
                                // Reserved: before class day a tap cancels; on class
                                // day offer Check In or Cancel.
                                if (isFutureClass) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleCancelReservation(athlete)}
                                      disabled={isLoading}
                                      title="Tap to cancel this reservation"
                                      sx={{ color: '#9C7A1E', borderColor: '#C9A84C', fontWeight: 600, '&:hover': { borderColor: '#9C7A1E', backgroundColor: 'rgba(201,168,76,0.1)' } }}
                                    >
                                      {isLoading ? '...' : 'Reserved \u2014 Cancel'}
                                    </Button>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleCheckIn(athlete)}
                                      disabled={isLoading}
                                      sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
                                    >
                                      {isLoading ? '...' : 'Check In'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleCancelReservation(athlete)}
                                      disabled={isLoading}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                );
                              })() : waitlisted ? (() => {
                                // Wait-listed: owners can leave any time; on class
                                // day staff can override-check-in or remove.
                                if (!isFutureClass && (isAdmin || isCoach)) {
                                  return (
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                      <Button size="small" variant="contained"
                                        onClick={() => handleCheckIn(athlete)} disabled={isLoading}
                                        sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}>
                                        {isLoading ? '...' : 'Check In'}
                                      </Button>
                                      <Button size="small" variant="outlined" color="error"
                                        onClick={() => handleCancelReservation(athlete)} disabled={isLoading}>
                                        Remove
                                      </Button>
                                    </Box>
                                  );
                                }
                                return (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCancelReservation(athlete)}
                                    disabled={isLoading}
                                    title="Tap to leave the wait-list"
                                    sx={{ color: '#7D3C98', borderColor: '#9B59B6', fontWeight: 600, '&:hover': { borderColor: '#7D3C98', backgroundColor: 'rgba(155,89,182,0.08)' } }}
                                  >
                                    {isLoading ? '...' : `Wait-list #${waitPos} \u2014 Leave`}
                                  </Button>
                                );
                              })() : (() => {
                                const playerBlocked = isParentOrAthlete && noBalance;
                                if (isFutureClass) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleReserve(athlete)}
                                      disabled={isLoading}
                                      title={classFull ? 'Class is full — join the wait-list and we\u2019ll email you if a spot opens' : 'Hold a spot — your session is deducted at check-in on class day'}
                                      sx={classFull
                                        ? { backgroundColor: '#9B59B6', color: '#fff', fontWeight: 700, '&:hover': { backgroundColor: '#7D3C98' } }
                                        : { backgroundColor: '#C9A84C', color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}
                                    >
                                      {isLoading ? '...' : classFull ? 'Join Wait-list' : 'Reserve'}
                                    </Button>
                                  );
                                }
                                return (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => !playerBlocked && handleCheckIn(athlete)}
                                    disabled={isLoading || playerBlocked}
                                    sx={{ 
                                      backgroundColor: playerBlocked ? '#555' : (noBalance ? '#E67E22' : '#1E3A5F'), 
                                      '&:hover': { backgroundColor: playerBlocked ? '#555' : (noBalance ? '#D35400' : '#2E6DA4') },
                                      border: (!playerBlocked && noBalance) ? '2px solid #E74C3C' : 'none',
                                      '&:disabled': { backgroundColor: playerBlocked ? '#555' : '#E67E22', color: 'white', opacity: 0.7 }
                                    }}
                                    title={playerBlocked ? 'No sessions remaining — ask a coach or admin' : (noBalance ? 'This will create a negative balance for tracking purposes' : '')}
                                  >
                                    {isLoading ? '...' : playerBlocked ? 'No sessions' : noBalance ? 'Check In (-)' : 'Check In'}
                                  </Button>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
      </Box>
    </Layout>
  );
}

export default CheckIn;
